import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { inflate } from 'zlib';
import { Store } from '@ngrx/store';

import { Product, ProductSubAssembly } from '../models';

import { allProductFields, allProductSubAssemblyFields } from '../../ngrx/graphql';

import { environment } from '../../environments/environment';
import { AuthenticationService } from './authentication.service';
import { CredentialService } from './credential.service';
import { OrderService } from './order.service';

const errorCodes = [400, 401, 403, 404];

@Injectable()
export class ProductService {
  constructor(
    private authService: AuthenticationService,
    private credentialService: CredentialService,
    private orderService: OrderService,
    private router: Router,
    private store: Store<any>
  ) {}

  private invokeProducts(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.magentoFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err.stack);
          } else {
            const output = typeof data.Payload === 'string'
                ? JSON.parse(data.Payload)
                :       data.Payload;
              if(output.gzip){
                const options = {
                  windowBits : 15
                };
                inflate(new Buffer(output.body,'base64'), options, (err, data) => {
                  if (err) {                    
                    observer.error(err.stack);
                  } else {
                    const unzippedOutput = Object.assign({}, output, {
                      body: data.toString()
                    });
                    observer.next(unzippedOutput);
                  }
                });
              } else {
                observer.next(output);
              }
          }
        }
      );
    });
  }

  private invokeProductMutation(payload: any): void {
    this.credentialService.lambda.invoke({
      FunctionName: environment.magentoFunction,
      Payload: JSON.stringify(payload)
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
            // this.authService.logout();
          }
        }
      }
    )
  }

  createProduct(product: Product): void {
    const formattedProduct = this.formatProduct(product);

    const variables = { product: formattedProduct };
    const query = `mutation($product: ProductInput!) {
      createProduct(product: $product) {
        ${allProductFields}
      }
    }`;

    this.invokeProductMutation({ query, variables });
  }

  deleteProduct(sku: string): void {
    const variables = { sku };
    const query = `mutation($sku: ID!) {
      deleteProduct(sku: $sku) {
        sku
      }
    }`;

    this.invokeProductMutation({ query, variables });
  }

  formatProduct(product: Product): any {
    const formattedProduct = Object.assign({}, product);

    if (formattedProduct.hasOwnProperty('createdAt')) delete formattedProduct['createdAt'];
    if (formattedProduct.hasOwnProperty('updatedAt')) delete formattedProduct['updatedAt'];

    return formattedProduct;
  }

  getProduct(sku: string): Observable<any> {
    const query = `{
      product(sku: "${sku}") {
        ${allProductFields}
      }
    }`;

    return this.invokeProducts({ query });
  }

  getProductList(): Observable<Product[]> {
    return this.store.select('products', 'productList');
  }

  getProductsBySKU(skus: string[]): Observable<any> {
    let formattedIDs = '';
    skus.forEach(sku => {
      formattedIDs += `"${sku}", `;
    });

    const query = `{
      productsBySKU(skus: [${formattedIDs.trim()}]) {
        ${allProductFields}
      }
    }`;

    return this.invokeProducts({ query });
  }

  updateProduct(product: Product): void {
    console.log(`updating product: ${JSON.stringify(product, null, 2)}`);
    if (product) {
      const formattedProduct = this.formatProduct(product);

      const variables = { product: formattedProduct };
      const query = `mutation($product: ProductInput!) {
        updateProduct(product: $product) {
          ${allProductFields}
        }
      }`;

      this.invokeProductMutation({ query, variables });
    }
  }

  updateProducts(products: Product[]): void {
    console.log(`updating products: ${JSON.stringify(products, null, 2)}`);
    if (products && products.length) {
      const formattedProducts = products.map(product => this.formatProduct(product));

      const variables = { products: formattedProducts };
      const query = `mutation($products: [ProductInput]) {
        updateProducts(products: $products) {
          ${allProductFields}
        }
      }`;

      this.invokeProductMutation({ query, variables });
    }
  }
}



// WEBPACK FOOTER //
// /Users/jlam2/impresocloud/www/client/src/app/services/product.service.ts